import * as React from "react";
import { AlertMessage, AlertType, ButtonType, CustomButton, CustomSubmitButton, Form, HiddenInput, Modal, ModalBody, ModalFooter, ModalSize, NumberInput, Select, TextInput, Textarea } from "soprasteria-ui-library";
import { useAuth } from "../../App";
import { nameOf } from "../../helpers/PropertyNameHelper";
import { IResidenceRequest } from "../../models/IResidenceRequest";
import { IListItem } from "soprasteria-ui-library/dist/models/IFormInput";
import { ResidenceApi } from "../../api/ResidenceApi";
import { emailAddressRegExp } from "../../helpers/RegExpHelper";
import { ResidenceRequestApi } from "../../api/ResidenceRequestApi";
import { setMessage } from "../../helpers/MessageHelper";
import { formatDate } from "../../helpers/DateHelper";

interface IProps {
    residenceRequest: IResidenceRequest | undefined;
    onSubmit: (residenceRequestId: number) => void;
    closeModal: () => void;
}

export const ResidenceRequestModal: React.FC<IProps> = ({ residenceRequest, onSubmit, closeModal }) => {
    const auth = useAuth();
    const api = new ResidenceRequestApi(auth);
    const residenceApi = new ResidenceApi(auth);
    const abortController = new AbortController();
    const [errorMessage, setErrorMessage] = React.useState<string>("");
    const [warningMessage, setWarningMessage] = React.useState<string>("");
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [residenceTypes, setResidenceTypes] = React.useState<IListItem[]>();
    const [selectedResidenceTypeId, setSelectedResidenceTypeId] = React.useState<string | undefined>(residenceRequest?.residenceTypeId ? residenceRequest.residenceTypeId.toString() : undefined);
    const [forceRefreshKey, setForceRefreshKey] = React.useState(0);

    React.useEffect(() => {
        document.getElementById("open-residence-request-modal")?.click();

        if (residenceRequest === undefined) {
            loadResidenceTypes();
        }

        return () => {
            abortController.abort();
        }
    }, [residenceRequest]);

    const loadResidenceTypes = () => {
        setIsLoading(true);
        residenceApi.getResidenceTypesForRegistration((data) => {
            const mappedResidenceTypes = data.map<IListItem>((residenceType) => {
                return {
                    text: residenceType.name,
                    value: residenceType.residenceTypeId.toString()
                };
            });
            mappedResidenceTypes.push({
                value: "-1",
                text: "Korttidsboende"
            });
            mappedResidenceTypes.push({
                value: "-1",
                text: "HVB-hem"
            });
            mappedResidenceTypes.sort((a, b) => a.text.localeCompare(b.text, 'sv'));
            setResidenceTypes(mappedResidenceTypes);
        }, undefined, () => setIsLoading(false));
    }

    const onResidenceTypeChange = (selectedValue?: string) => {
        if (selectedValue === "-1") {
            setForceRefreshKey(prevKey => prevKey + 1);
            const newMessage = setMessage("Ej möjligt att lägga till boendeenhetet som är av typen HVB-hem eller Korttidsboenden", warningMessage);
            setWarningMessage(newMessage);
        }
    }

    const onFormSubmit = (data: IResidenceRequest) => {
        setIsLoading(true);
        if (data.residenceRequestId.toString() === "0") {
            api.createResidenceRequest(data, (createdResidenceRequestId) => {
                onSubmit(createdResidenceRequestId);
            }, (message: string) => {
                const newMessage = setMessage(message, errorMessage);
                setErrorMessage(newMessage);
            }, () => setIsLoading(false));
        } else {
            console.log("not implemented yet");
        }
    }

    const isRequired = () => {
        return residenceRequest === undefined;
    }

    const isReadonly = () => {
        return !isRequired();
    }

    return (
        <Modal id="residence-request-modal" modalSize={ModalSize.extraLarge} header={residenceRequest ? "Visa ansökan" : "Skapa ansökan"}>
            <Form onSubmit={onFormSubmit}>
                <ModalBody>
                    <AlertMessage alertType={AlertType.danger} duration={5000} >{errorMessage}</AlertMessage>
                    <AlertMessage alertType={AlertType.warning} duration={5000} >{warningMessage}</AlertMessage>
                    <div className="row">
                        <div className="col-12">
                            <h4>Boendeuppgifter</h4>
                        </div>
                        <div className="col-6">
                            <HiddenInput name={nameOf<IResidenceRequest>('residenceRequestId')} value={residenceRequest?.residenceRequestId ? residenceRequest.residenceRequestId.toString() : "0"} />
                            <TextInput label="Boendenamn" name={nameOf<IResidenceRequest>('residenceName')} defaultValue={residenceRequest?.residenceName} readonly={isReadonly()} required={isRequired()} inlineLabel />
                            {
                                residenceRequest === undefined ?
                                    <Select key={forceRefreshKey} label="Boendetyp" name={nameOf<IResidenceRequest>('residenceTypeId')} placeholder="Välj en boendetyp" options={residenceTypes ?? []} selectedValue={selectedResidenceTypeId} onChange={onResidenceTypeChange} required inlineLabel />
                                    : <TextInput label="Boendetyp" name="readonly" defaultValue={residenceRequest?.residenceTypeName ? residenceRequest.residenceTypeName : ""} readonly inlineLabel />
                            }
                            <NumberInput label="Platsantal" name={nameOf<IResidenceRequest>('capacity')} placeholder="st 2024" minValue={0} maxValue={999} defaultValue={residenceRequest?.capacity ? residenceRequest.capacity.toString() : ""} readonly={isReadonly()} required={isRequired()} inlineLabel />
                        </div>
                        {
                            residenceRequest !== undefined &&
                            <>
                                <div className="col-6">
                                    <TextInput label="Status" name="readonly" defaultValue={residenceRequest.statusName} readonly inlineLabel />
                                    <TextInput label="Beslutsdatum" name="readonly" defaultValue={formatDate(residenceRequest.decisionDate)} readonly inlineLabel />
                                    <TextInput label="Beslut" name="readonly" defaultValue={residenceRequest.decisionTypeName ?? "-"} readonly inlineLabel />
                                    {
                                        residenceRequest.decisionMotivation &&
                                        <Textarea label="Motivering" name="readonly" defaultValue={residenceRequest.decisionMotivation ?? "-"} readonly inlineLabel />
                                    }                                    
                                </div>
                            </>
                        }
                        <div className="col-12">
                            <hr />
                            <h4>Boendeadress</h4>
                        </div>
                        <div className="col-6">
                            <HiddenInput name={nameOf<IResidenceRequest>('residenceRequestAddressPostalAddressId')} value={residenceRequest?.residenceRequestAddressPostalAddressId ? residenceRequest.residenceRequestAddressPostalAddressId.toString() : "0"} />
                            <TextInput label="Postadress" name={nameOf<IResidenceRequest>('residenceRequestAddressAddress')} defaultValue={residenceRequest?.residenceRequestAddressAddress ?? ""} readonly={isReadonly()} required={isRequired()} inlineLabel />
                            <TextInput label="Postnummer" name={nameOf<IResidenceRequest>('residenceRequestAddressZipCode')} defaultValue={residenceRequest?.residenceRequestAddressZipCode ?? ""} readonly={isReadonly()} required={isRequired()} inlineLabel />
                            <TextInput label="Postort" name={nameOf<IResidenceRequest>('residenceRequestAddressCity')} defaultValue={residenceRequest?.residenceRequestAddressCity ?? ""} readonly={isReadonly()} required={isRequired()} inlineLabel />
                        </div>
                        <div className="col-6">
                            <TextInput label="Telefonnummer" name={nameOf<IResidenceRequest>('residenceRequestAddressPhoneNumber')} defaultValue={residenceRequest?.residenceRequestAddressPhoneNumber ?? ""} readonly={isReadonly()} required={isRequired()} inlineLabel />
                            <TextInput label="Alt. telefonnummer" name={nameOf<IResidenceRequest>('residenceRequestAddressAlternativePhoneNumber')} defaultValue={residenceRequest?.residenceRequestAddressAlternativePhoneNumber ?? ""} readonly={isReadonly()} inlineLabel />
                        </div>
                        <div className="col-12">
                            <hr />
                            <h4>Ansvarig person för boendeenhet</h4>
                        </div>
                        <div className="col-6">
                            <TextInput label="För- och efternamn" name={nameOf<IResidenceRequest>('responsibleFullName')} defaultValue={residenceRequest?.responsibleFullName ?? ""} readonly={isReadonly()} required={isRequired()} inlineLabel />
                            <TextInput label="E-postadress" name={nameOf<IResidenceRequest>('responsibleEmailAddress')} maxLength={50} pattern={emailAddressRegExp()} patternValidationMessage="Ogiltigt format på e-postadress" defaultValue={residenceRequest?.responsibleEmailAddress ?? ""} readonly={isReadonly()} required={isRequired()} inlineLabel />
                        </div>
                        <div className="col-6">

                            <TextInput label="Telefonnummer" name={nameOf<IResidenceRequest>('responsiblePhoneNumber')} defaultValue={residenceRequest?.responsiblePhoneNumber ?? ""} readonly={isReadonly()} required={isRequired()} inlineLabel />
                            <TextInput label="Alt. telefonnummer" name={nameOf<IResidenceRequest>('responsibleAlternativePhoneNumber')} defaultValue={residenceRequest?.responsibleAlternativePhoneNumber ?? ""} readonly={isReadonly()} inlineLabel />
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <div className="text-right btn-group" role="group">
                        <CustomButton buttonType={ButtonType.general} id="close-residence-request-modal" data-dismiss="modal" onClick={() => closeModal()} buttonText={residenceRequest ? "Stäng" : "Avbryt"} />
                        <CustomSubmitButton isLoading={isLoading} hidden={residenceRequest !== undefined}>Ansök</CustomSubmitButton>
                        <input type="hidden" data-toggle="modal" id="open-residence-request-modal" data-target="#residence-request-modal" />
                    </div>
                </ModalFooter>
            </Form>
        </Modal>
    );
}